/**
 * useOnlineStatus Hook
 *
 * Detects online/offline status for both browser and Capacitor native platforms.
 * - Browser: navigator.onLine + online/offline events
 * - Capacitor: Network.getStatus() + networkStatusChange listener
 */

import { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { Network } from '@capacitor/network';

export function useOnlineStatus(): boolean {
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);

  useEffect(() => {
    const isNative = Capacitor.isNativePlatform();

    if (isNative) {
      // Capacitor native: use Network plugin
      Network.getStatus()
        .then((status) => setIsOnline(status.connected))
        .catch(() => {});

      const listenerPromise = Network.addListener('networkStatusChange', (status) => {
        setIsOnline(status.connected);
      });

      return () => {
        listenerPromise.then((l) => l.remove()).catch(() => {});
      };
    } else {
      // Browser: use native events
      const goOnline = () => setIsOnline(true);
      const goOffline = () => setIsOnline(false);

      window.addEventListener('online', goOnline);
      window.addEventListener('offline', goOffline);

      return () => {
        window.removeEventListener('online', goOnline);
        window.removeEventListener('offline', goOffline);
      };
    }
  }, []);

  return isOnline;
}
